import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { BrandSplash } from '@/components/system/BrandSplash';
import { ForbiddenState } from '@/components/system/AccessState';
import { safeReturnPath } from '@/utils/navigation';

/**
 * Requires a signed-in customer — BUILD-PLAN §17, §55.
 *
 * A UX control, not a security boundary: every protected endpoint authorizes
 * server-side, so bypassing this in the browser yields empty screens rather
 * than data.
 */

interface ProtectedRouteProps {
  children: ReactNode;
  /** Also require a specific permission (used by the admin area). */
  permission?: string;
  /** Require a confirmed email address. */
  requireVerified?: boolean;
}

export function ProtectedRoute({
  children,
  permission,
  requireVerified = false,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, isVerified, can } = useAuth();
  const location = useLocation();

  // Wait for the session check. Redirecting first would bounce an already
  // signed-in customer to the login page on every hard refresh.
  if (isLoading) {
    return <BrandSplash />;
  }

  if (!isAuthenticated) {
    /*
     | Redirect rather than render the unauthorized state, so the customer
     | lands on a real /login URL they can bookmark or reload. `returnTo`
     | brings them back afterwards, and `replace` keeps the protected URL out
     | of history so Back does not bounce them straight out again.
     */
    return (
      <Navigate
        to={`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  }

  if (requireVerified && !isVerified) {
    return <Navigate to="/verify-email" replace />;
  }

  if (permission && !can(permission)) {
    return <ForbiddenState />;
  }

  return <>{children}</>;
}

/**
 * The inverse: keeps a signed-in customer off the sign-in and registration
 * pages, which would otherwise be a confusing dead end.
 */
export function GuestRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <BrandSplash />;
  }

  if (isAuthenticated) {
    const returnTo = new URLSearchParams(location.search).get('returnTo');

    // Sanitised because returnTo is attacker-controlled — see safeReturnPath.
    return <Navigate to={safeReturnPath(returnTo, '/account')} replace />;
  }

  return <>{children}</>;
}
